import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { progressService } from '../api/services/progress';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, TooltipProps } from 'recharts';
import { format } from 'date-fns';
import Dropdown from '../components/Dropdown';

interface TopicProgress {
  id: number;
  name: string;
  daily_progress: {
    [date: string]: {
      date: string;
      average_mastery: number;
      items_practiced: number;
      accuracy: number;
      sessions: number;
    };
  };
  overall_improvement: number;
  current_mastery: number;
}

interface Topic {
  id: number;
  name: string;
  subtopics: Subtopic[];
}

interface Subtopic {
  id: number;
  name: string;
  microtopics: Microtopic[];
}

interface Microtopic {
  id: number;
  name: string;
}

interface MicrotopicProgress {
  id: number;
  name: string;
  daily_progress: {
    [date: string]: {
      date: string;
      average_mastery: number;
      accuracy: number;
    };
  };
  overall_improvement: number;
  current_mastery: number;
}

const CustomTooltip = ({ active, payload, label }: TooltipProps<number, string>) => {
  if (active && payload && payload.length) {
    const currentValue = payload[0].value as number;
    const previousValue = payload[0].payload.previousValue;
    const isIncrease = previousValue !== undefined && currentValue > previousValue;
    const hasChange = previousValue !== undefined && currentValue !== previousValue;

    return (
      <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
        <p className="text-sm text-gray-600 dark:text-gray-400">{label}</p>
        <p className="text-m font-semibold text-gray-900 dark:text-white">
          {currentValue.toFixed(1)}
          {hasChange && (
            <span className={`ml-1 ${isIncrease ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {isIncrease ? '▲' : '▼'}
            </span>
          )}
        </p>
      </div>
    );
  }
  return null;
};

const MicrotopicProgressChart = React.memo(({ progress }: { progress: MicrotopicProgress }) => (
  <div className="mt-6">
    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
      {progress.name} Progress
    </h3>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
        <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Current Mastery</div>
        <div className="text-2xl font-semibold text-indigo-600 dark:text-indigo-400">
          {(progress.current_mastery ?? 0).toFixed(1)}/5
        </div>
      </div>

      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
        <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Average Accuracy</div>
        <div className="text-2xl font-semibold text-green-600 dark:text-green-400">
          {((Object.values(progress.daily_progress || {}).reduce((acc, day) => acc + (day.accuracy || 0), 0) /
            (Object.keys(progress.daily_progress || {}).length || 1)) || 0).toFixed(1)}%
        </div>
      </div>
    </div>

    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={Object.values(progress.daily_progress || {}).map((day, index, array) => ({
            ...day,
            date: format(new Date(day.date), 'MMM d'),
            average_mastery: day.average_mastery ?? 0,
            previousValue: index > 0 ? array[index - 1].average_mastery : undefined
          }))}
          margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
        >
          <CartesianGrid vertical={false} stroke="#84878b" strokeOpacity={0.2} />
          <XAxis dataKey="date" />
          <YAxis domain={[0, 5]} ticks={[0, 1, 2, 3, 4, 5]} width={30} />
          <Tooltip content={<CustomTooltip />} />
          <Line
            type="monotone"
            dataKey="average_mastery"
            stroke="#6366f1"
            name="Mastery Level"
            strokeWidth={2}
            dot={false}
            activeDot={true}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  </div>
));

MicrotopicProgressChart.displayName = 'MicrotopicProgressChart';

const MicrotopicSelector = React.memo(({
  topicData,
  selectedTopic,
  selectedSubtopic,
  selectedMicrotopic,
  onTopicChange,
  onSubtopicChange,
  onMicrotopicChange
}: {
  topicData: Topic[];
  selectedTopic: Topic | null;
  selectedSubtopic: Subtopic | null;
  selectedMicrotopic: Microtopic | null;
  onTopicChange: (value: string) => void;
  onSubtopicChange: (value: string) => void;
  onMicrotopicChange: (value: string) => void;
}) => {
  const topicOptions = useMemo(() =>
    topicData.map(topic => ({
      value: topic.id,
      label: topic.name
    })), [topicData]);

  const subtopicOptions = useMemo(() =>
    selectedTopic?.subtopics.map(subtopic => ({
      value: subtopic.id,
      label: subtopic.name
    })) || [], [selectedTopic]);

  const microtopicOptions = useMemo(() =>
    selectedSubtopic?.microtopics.map(microtopic => ({
      value: microtopic.id,
      label: microtopic.name
    })) || [], [selectedSubtopic]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
      <Dropdown
        label="Topic"
        value={selectedTopic?.id || ''}
        onChange={onTopicChange}
        options={topicOptions}
      />

      <Dropdown
        label="Subtopic"
        value={selectedSubtopic?.id || ''}
        onChange={onSubtopicChange}
        options={subtopicOptions}
        disabled={!selectedTopic}
      />

      <Dropdown
        label="Microtopic"
        value={selectedMicrotopic?.id || ''}
        onChange={onMicrotopicChange}
        options={microtopicOptions}
        disabled={!selectedSubtopic}
      />
    </div>
  );
});

MicrotopicSelector.displayName = 'MicrotopicSelector';

const Progress: React.FC = () => {
  const { user } = useAuth();
  const [topics, setTopics] = useState<TopicProgress[]>([]);
  const [topicData, setTopicData] = useState<Topic[]>([]);
  const [selectedTopic, setSelectedTopic] = useState<Topic | null>(null);
  const [selectedSubtopic, setSelectedSubtopic] = useState<Subtopic | null>(null);
  const [selectedMicrotopic, setSelectedMicrotopic] = useState<Microtopic | null>(null);
  const [microtopicProgress, setMicrotopicProgress] = useState<MicrotopicProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const microtopicSectionRef = useRef<HTMLDivElement>(null);
  const hasFetched = useRef(false);
  const isFetching = useRef(false);

  const handleTopicChange = useCallback((value: string) => {
    const topic = topicData.find(t => t.id === parseInt(value));
    setSelectedTopic(topic || null);
    setSelectedSubtopic(null);
    setSelectedMicrotopic(null);
  }, [topicData]);

  const handleSubtopicChange = useCallback((value: string) => {
    const subtopic = selectedTopic?.subtopics.find(s => s.id === parseInt(value));
    setSelectedSubtopic(subtopic || null);
    setSelectedMicrotopic(null);
  }, [selectedTopic]);

  const handleMicrotopicChange = useCallback((value: string) => {
    const microtopic = selectedSubtopic?.microtopics.find(m => m.id === parseInt(value));
    setSelectedMicrotopic(microtopic || null);
  }, [selectedSubtopic]);

  useEffect(() => {
    const fetchData = async () => {
      if (isFetching.current || hasFetched.current) return;

      try {
        isFetching.current = true;
        setLoading(true);
        const topicsResponse = await progressService.getTopics();
        if (!topicsResponse.data?.success || !topicsResponse.data.data) {
          throw new Error('Invalid response format');
        }
        setTopicData(topicsResponse.data.data);

        const progressPromises = topicsResponse.data.data.map(async (topic: any) => {
          const progressResponse = await progressService.getTopicProgress(topic.id);
          if (!progressResponse.data?.success || !progressResponse.data.data) {
            throw new Error('Invalid progress response format');
          }
          // Round off the values to one decimal place
          const dailyProgress = Object.entries(progressResponse.data.data.daily_progress || {}).reduce((acc, [date, data]: [string, any]) => ({
            ...acc,
            [date]: {
              ...data,
              average_mastery: Number(data.average_mastery.toFixed(1)),
              accuracy: Number(data.accuracy.toFixed(1))
            }
          }), {});

          return {
            id: topic.id,
            name: topic.name,
            ...progressResponse.data.data,
            daily_progress: dailyProgress,
            current_mastery: Number(progressResponse.data.data.current_mastery.toFixed(1))
          };
        });

        const topicsWithProgress = await Promise.all(progressPromises);
        setTopics(topicsWithProgress);
        hasFetched.current = true;
      } catch (err) {
        setError('Failed to load progress data');
        console.error('Error fetching progress:', err);
      } finally {
        setLoading(false);
        isFetching.current = false;
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchMicrotopicProgress = async () => {
      if (selectedMicrotopic) {
        try {
          setLoading(true);
          const response = await progressService.getMicrotopicProgress(selectedMicrotopic.id);
          if (!response.data?.success || !response.data.data) {
            throw new Error('Invalid microtopic progress response format');
          }
          // Round off the values to one decimal place
          const dailyProgress = Object.entries(response.data.data.daily_progress || {}).reduce((acc, [date, data]: [string, any]) => ({
            ...acc,
            [date]: {
              ...data,
              average_mastery: Number(data.average_mastery.toFixed(1)),
              accuracy: Number(data.accuracy.toFixed(1))
            }
          }), {});

          setMicrotopicProgress({
            id: selectedMicrotopic.id,
            name: selectedMicrotopic.name,
            ...response.data.data,
            daily_progress: dailyProgress,
            current_mastery: Number(response.data.data.current_mastery.toFixed(1))
          });
        } catch (err) {
          setError('Failed to load microtopic progress');
          console.error('Error fetching microtopic progress:', err);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchMicrotopicProgress();
  }, [selectedMicrotopic]);

  useEffect(() => {
    if (microtopicProgress && microtopicSectionRef.current) {
      // Smooth scroll to the chart section when it appears
      microtopicSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [microtopicProgress]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">📈 It's always good to track your progress! 🚀</h1>

      {/* Mastery Level Descriptions */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="font-medium text-gray-900 dark:text-white mb-4">Mastery Levels:</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">0</span>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Not Started</p>
          </div>
          <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">1</span>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Beginner</p>
          </div>
          <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">2</span>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Basic</p>
          </div>
          <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">3</span>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Intermediate</p>
          </div>
          <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">4</span>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Advanced</p>
          </div>
          <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">5</span>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Mastered</p>
          </div>
        </div>
      </div>

      {/* Topic Progress Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {topics.map((topic) => (
          <div key={topic.id} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">{topic.name}</h2>

            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-400">Current Mastery</span>
                <span className="text-lg font-medium text-indigo-600 dark:text-indigo-400">
                  {topic.current_mastery.toFixed(1)}/5
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-400">Average Accuracy</span>
                <span className="text-lg font-medium text-green-600 dark:text-green-400">
                  {(Object.values(topic.daily_progress).reduce((acc, day) => acc + day.accuracy, 0) / Object.keys(topic.daily_progress).length || 0).toFixed(1)}%
                </span>
              </div>
            </div>

            <div className="mt-6 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={Object.values(topic.daily_progress).map((day, index, array) => ({
                    ...day,
                    date: format(new Date(day.date), 'MMM d'),
                    previousValue: index > 0 ? array[index - 1].average_mastery : undefined
                  }))}
                  margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
                >
                  <CartesianGrid vertical={false} stroke="#84878b" strokeOpacity={0.2} />
                  <XAxis dataKey="date" />
                  <YAxis domain={[0, 5]} ticks={[0, 1, 2, 3, 4, 5]} width={30} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="average_mastery"
                    stroke="#6366f1"
                    name="Mastery Level"
                    strokeWidth={2}
                    dot={false}
                    activeDot={true}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        ))}
      </div>

      {/* Microtopic Progress Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">📌 Nail down your progress</h2>

        <MicrotopicSelector
          topicData={topicData}
          selectedTopic={selectedTopic}
          selectedSubtopic={selectedSubtopic}
          selectedMicrotopic={selectedMicrotopic}
          onTopicChange={handleTopicChange}
          onSubtopicChange={handleSubtopicChange}
          onMicrotopicChange={handleMicrotopicChange}
        />

        {microtopicProgress && <MicrotopicProgressChart progress={microtopicProgress} />}
      </div>
    </div>
  );
};

export default Progress;